import {type ComponentType} from 'react'
import {type ZodSchema} from 'zod'

import {type Quiz} from '#/types/quiz'

export type SlideType =
  | 'video'
  | 'single_choice'
  | 'multiple_choice'
  | 'true_false'
  | 'matching'
  | 'fill_blank'
  | 'content'
  | 'ordering'
  | 'image_choice'
  | 'accordion'
  | 'drag_words'
  | 'find_words'
  | 'mark_words'
  | 'complex_fill_blank'
  | 'fill_blanks' // H5P: Fill in the Blanks - inline paragraph with ___ markers
  | 'text_block' // Rich text / bullet-point slide
  | 'image_slide' // Image with caption
  | 'table_slide' // Lightweight table / grid
  | 'chart_slide' // Bar, Line or Pie chart
  | 'formula_slide' // LaTeX formula
  | 'drawing_slide' // Freehand drawing

// ── Theme categories ──────────────────────────────────────────────────────────

export const THEME_CATEGORIES = [
  {
    id: 'classic',
    label: 'Classic',
    emoji: '📚',
    description: 'Clean educational layouts',
  },
  {
    id: 'games',
    label: 'Games',
    emoji: '🎮',
    description: 'Gaming-inspired experiences',
  },
  {
    id: 'cinema',
    label: 'Cinema',
    emoji: '🎬',
    description: 'Film & series universes',
  },
  {
    id: 'minimal',
    label: 'Minimal',
    emoji: '✦',
    description: 'Distraction-free design',
  },
  {
    id: 'missions',
    label: 'Missions',
    emoji: '🚀',
    description: 'Adventure & quest formats',
  },
  {
    id: 'science',
    label: 'Science',
    emoji: '🔬',
    description: 'Lab, space & tech themes',
  },
  {
    id: 'nature',
    label: 'Nature',
    emoji: '🌿',
    description: 'Organic & calming vibes',
  },
  {
    id: 'custom',
    label: 'Custom',
    emoji: '⬆️',
    description: 'Upload your own HTML theme',
  },
] as const

export type ThemeCategory = (typeof THEME_CATEGORIES)[number]['id']

export type AccordionItem = {
  id: string
  title: string
  body: string
}

export type ComplexBlank = {
  id: string
  sentence: string
  answer: string
  options: string[]
  alternatives?: string[]
}

export type SlideOption = {
  id: string
  label: string
  imageUrl?: string
  isCorrect: boolean
}

export type MatchPair = {
  id: string
  left: string
  right: string
}

export type FillBlank = {
  id: string
  before: string
  answer: string
  after: string
  alternatives?: string[]
}

export type Slide = {
  id: string
  type: SlideType

  // video
  videoUrl?: string
  videoThumbnailUrl?: string
  videoSource?: number
  /**
   * How long the cover film runs, when the platform measured it.
   *
   * The studio needs this and not the file: whether a film is longer than a cover
   * may run is the whole question the trimmer answers (see `#/lib/coverTrim`), and
   * a record loaded back from a draft carries the length it was saved with rather
   * than a URI whose duration nobody has read.
   */
  videoDurationMs?: number
  /**
   * Which minute of a long film the cover opens on.
   *
   * A capsule cover may run for three minutes; a longer film is not refused, its
   * window is chosen. Absent means the film plays from its beginning.
   */
  videoStartMs?: number

  // content
  imageUrl?: string
  contentText?: string
  contentTitle?: string
  // reading audio for content/text blocks (FlashBook)
  readingAudioUrl?: string
  /**
   * How a `content` block presents itself in a FlashBook. Omitted/`text` is the
   * plain title + prose block; the rest are the *rich reading blocks* (a pull
   * quote, a note, a captioned figure, a data table, a formula) that each theme
   * treats differently. Keeping them on `content` means the whole rich family
   * reuses one editor, one audio field and one stored shape.
   */
  contentKind?: 'text' | 'quote' | 'callout' | 'caption' | 'table' | 'formula'
  /** Quote attribution, or the credit on a captioned figure. */
  contentAuthor?: string
  /** Which mark a callout wears — the theme colours it. */
  calloutTone?: 'info' | 'success' | 'warning'

  // exercise common
  question?: string
  explanation?: string
  points?: number
  timing?: number

  // single_choice / multiple_choice / image_choice
  options?: SlideOption[]

  // true_false
  answer?: string // "true" | "false"

  // matching
  pairs?: MatchPair[]

  // fill_blank
  blanks?: FillBlank[]

  // ordering
  orderItems?: Array<{id: string; label: string}>
  correctOrder?: string[]

  // accordion
  accordionItems?: AccordionItem[]

  // drag_words
  dragWordsTemplate?: string
  dragWordsBank?: string[]

  // find_words
  findWordsTargets?: string[]
  findWordsGrid?: string[][]

  // mark_words
  markWordsText?: string
  markWordsCorrect?: string[]

  // complex_fill_blank
  complexBlanks?: ComplexBlank[]

  // fill_blanks (H5P inline paragraph style)
  fillBlanksParagraph?: string
  fillBlanksAnswers?: Array<{answer: string; alternatives?: string[]}>

  // table_slide
  tableRows?: string[][]
  tableCols?: number

  // chart_slide
  chartData?: Array<{label: string; value: number}>

  // formula_slide
  latex?: string
}

export type SlideResult = {
  slideId: string
  type: SlideType
  isCorrect?: boolean
  timeSpentMs: number
  userAnswer?: unknown
  points?: number
}

export type CapsuleRecentPlay = {
  player: {
    did: string
    handle: string
    displayName?: string
    avatar?: string
  }
  score: number
  timeMs: number
  createdAt: string
}

export type CapsuleView = {
  uri: string
  cid: string
  title: string
  description?: string
  status: 'draft' | 'published'
  themeId?: string
  /** The bed, stored apart from the theme; see `#/lib/backgroundMusic`. */
  backgroundMusicId?: string
  behaviorSettings?: Record<string, unknown>
  slides: Slide[]
  slideCount?: number
  subject?: string
  level?: string
  langs?: string[]
  tags?: string[]
  post: {uri: string; cid: string}
  playCount?: number
  recentPlays?: CapsuleRecentPlay[]
}

export const SLIDE_TYPE_META: Record<
  SlideType,
  {
    label: string
    isExercise: boolean
    supported: boolean
    color: string
    icon: string
  }
> = {
  video: {
    label: 'Video',
    isExercise: false,
    supported: false,
    color: '#6366f1',
    icon: 'video',
  },
  single_choice: {
    label: 'Single Choice',
    isExercise: true,
    supported: true,
    color: '#0085ff',
    icon: 'check-circle',
  },
  multiple_choice: {
    label: 'Multiple Choice',
    isExercise: true,
    supported: true,
    color: '#8b5cf6',
    icon: 'check-square',
  },
  true_false: {
    label: 'True / False',
    isExercise: true,
    supported: true,
    color: '#10b981',
    icon: 'toggle-left',
  },
  matching: {
    label: 'Matching',
    isExercise: true,
    supported: true,
    color: '#f59e0b',
    icon: 'link-2',
  },
  fill_blank: {
    label: 'Fill in the Blank',
    isExercise: true,
    supported: true,
    color: '#ec4899',
    icon: 'edit-2',
  },
  content: {
    label: 'Content',
    isExercise: false,
    supported: true,
    color: '#64748b',
    icon: 'file-text',
  },
  ordering: {
    label: 'Ordering',
    isExercise: true,
    supported: true,
    color: '#f97316',
    icon: 'list',
  },
  image_choice: {
    label: 'Image Choice',
    isExercise: true,
    supported: true,
    color: '#06b6d4',
    icon: 'image',
  },
  accordion: {
    label: 'Accordion',
    isExercise: false,
    supported: true,
    color: '#84cc16',
    icon: 'chevrons-down',
  },
  drag_words: {
    label: 'Drag Words',
    isExercise: true,
    supported: true,
    color: '#a78bfa',
    icon: 'move',
  },
  find_words: {
    label: 'Find Words',
    isExercise: true,
    supported: true,
    color: '#fb923c',
    icon: 'search',
  },
  mark_words: {
    label: 'Mark Words',
    isExercise: true,
    supported: true,
    color: '#34d399',
    icon: 'tag',
  },
  complex_fill_blank: {
    label: 'Complex Fill Blank',
    isExercise: true,
    supported: true,
    color: '#f472b6',
    icon: 'edit-3',
  },
  fill_blanks: {
    label: 'Fill in blanks',
    isExercise: true,
    supported: true,
    color: '#ec4899',
    icon: 'edit-2',
  },
  text_block: {
    label: 'Text block',
    isExercise: false,
    supported: false,
    color: '#94a3b8',
    icon: 'align-left',
  },
  image_slide: {
    label: 'Image',
    isExercise: false,
    supported: false,
    color: '#06b6d4',
    icon: 'image',
  },
  table_slide: {
    label: 'Table',
    isExercise: false,
    supported: false,
    color: '#22c55e',
    icon: 'grid',
  },
  chart_slide: {
    label: 'Chart',
    isExercise: false,
    supported: false,
    color: '#f59e0b',
    icon: 'bar-chart-2',
  },
  formula_slide: {
    label: 'Formula',
    isExercise: false,
    supported: false,
    color: '#8b5cf6',
    icon: 'code',
  },
  drawing_slide: {
    label: 'Drawing',
    isExercise: false,
    supported: false,
    color: '#ec4899',
    icon: 'pen-tool',
  },
}

/**
 * The stable key a block is addressed by in tests and analytics.
 *
 * A rich reading block is a `content` slide *with a presentation*, so both the
 * studio and the reader have to name it as such: without this, a quote and a
 * plain text block are the same `content` the moment they are rendered, and
 * nothing that walks the block list can tell whether the rich ones appeared at
 * all.
 */
export function slideBlockKey(slide: Slide): string {
  return slide.type === 'content' &&
    slide.contentKind &&
    slide.contentKind !== 'text'
    ? `content:${slide.contentKind}`
    : slide.type
}

let _slideIdCounter = 0
function nextSlideId() {
  _slideIdCounter += 1
  return `s_${Date.now().toString(36)}_${_slideIdCounter}`
}

export function quizToSlide(quiz: Quiz): Slide {
  const id = nextSlideId()
  if (quiz.contentType === 'TrueFalse') {
    return {
      id,
      type: 'true_false',
      question: quiz.question,
      answer: quiz.correctAnswer ? 'true' : 'false',
    }
  }
  if (quiz.contentType === 'SingleChoiceSet') {
    return {
      id,
      type: 'single_choice',
      question: quiz.question,
      options: quiz.options.map((o, i) => ({
        id: `o_${i}`,
        label: o.text,
        isCorrect: o.correct,
      })),
    }
  }
  if (quiz.contentType === 'MultiChoice') {
    return {
      id,
      type: 'multiple_choice',
      question: quiz.question,
      options: quiz.options.map((o, i) => ({
        id: `o_${i}`,
        label: o.text,
        isCorrect: o.correct,
      })),
    }
  }
  // Unsupported quiz content types fall back to a plain content slide.
  return {
    id,
    type: 'content',
    contentText: quiz.question,
  }
}

export function slideToQuiz(
  slide: Slide,
  orderInCollection: number,
): Quiz | null {
  if (slide.type === 'true_false') {
    return {
      contentType: 'TrueFalse',
      orderInCollection,
      question: slide.question ?? '',
      correctAnswer: slide.answer === 'true',
    }
  }
  if (slide.type === 'single_choice') {
    return {
      contentType: 'SingleChoiceSet',
      orderInCollection,
      question: slide.question ?? '',
      options: (slide.options ?? []).map(o => ({
        text: o.label,
        correct: o.isCorrect,
      })),
    }
  }
  if (slide.type === 'multiple_choice') {
    return {
      contentType: 'MultiChoice',
      orderInCollection,
      question: slide.question ?? '',
      options: (slide.options ?? []).map(o => ({
        text: o.label,
        correct: o.isCorrect,
      })),
    }
  }
  if (slide.type === 'matching') {
    return {
      contentType: 'Matching',
      orderInCollection,
      question: slide.question ?? '',
      pairs: (slide.pairs ?? []).map(p => ({
        id: p.id,
        left: p.left,
        right: p.right,
      })),
    }
  }
  if (slide.type === 'ordering') {
    return {
      contentType: 'Ordering',
      orderInCollection,
      question: slide.question ?? '',
      items: (slide.orderItems ?? []).map(i => ({id: i.id, label: i.label})),
      correctOrder: slide.correctOrder ?? [],
    }
  }
  return null
}

// ── Capsule model used by themes ─────────────────────────────────────────────
export type CapsuleModel = {
  id: string
  title: string
  description: string
  creatorId: string
  themeId: string
  backgroundMusicId?: string
  status: 'draft' | 'published'
  slides: Slide[]
  behaviorSettings: Record<string, unknown>
  metadata: {createdAt: string; updatedAt: string}
  likeCount: number
  likedByMe: boolean
}

// ── Legacy page type used by theme PlayerComponents ───────────────────────────
export type Page = {
  id: string
  type: string
  question: string
  options?: {label: string; isCorrect: boolean}[]
  answer?: string
  explanation?: string
  timing?: number
}

export type PageResult = {
  pageId: string
  isCorrect: boolean
  timeSpentMs: number
  userAnswer: unknown
}

// ── Theme Protocol ────────────────────────────────────────────────────────────
export interface ThemePlayerProps {
  capsule: CapsuleModel
  currentPageIndex: number
  onPageComplete: (result: PageResult) => void
  onCapsuleComplete: (results: PageResult[]) => void
}

export interface ThemeEditorProps {
  capsule: CapsuleModel
  onCapsuleUpdate: (updated: Partial<CapsuleModel>) => void
  onAddPage: (type: string) => void
  onDeletePage: (pageId: string) => void
  onPageUpdate: (pageId: string, data: Partial<Page>) => void
  onSettingsUpdate: (settings: Record<string, unknown>) => void
}

export interface ThemePlugin {
  id: string
  name: string
  version: string
  description: string
  previewColor: string
  category: ThemeCategory
  exerciseTypes: {
    type: SlideType | string
    label: string
    icon: string
    schema: ZodSchema
  }[]
  settingsSchema: ZodSchema
  PlayerComponent: ComponentType<ThemePlayerProps>
  EditorComponent: ComponentType<ThemeEditorProps>
  PreviewComponent: ComponentType<ThemePlayerProps>
}
